// AllCustomersPage — every customer this service holds, and what they hold.
//
// ONE TABLE. Every customer is a row in it, told apart by a Status column:
// Active, Retired (the relationship ended — `retire_customer`), or Superseded
// (its instance was re-parented onto the customer that replaced it —
// `supersede_customer`). Retired and superseded customers are both kept, not
// deleted, and both are FORMER customers: hidden by default, because this list
// is who we serve, and brought back together under "Show former customers".
// There used to be a second table, "No longer active", holding the superseded
// rows while the retired ones hid behind the toggle; two filing systems for
// one kind of fact is what that was, and it is gone.
//
// The Status column draws the two facts the service carries on the row —
// `retired` and `superseded_by` — and nothing it infers. A customer with no
// subscriptions is a customer who has not deployed yet, not a former one.
//
// **This screen is not "Fleet Management".** That is a live customer-facing
// screen showing one customer their own instances. This one shows every
// customer, and the two are told apart by their names rather than by renaming
// the screen customers already use.
//
// Opening it writes five rows into the see-all audit trail, one per table
// read, under one shared reason. The lead says so, and links to the trail,
// because a surface that logs the people using it should say so to their
// faces. Flipping the toggle is a second screen-open in the trail, which is
// what it is.
//
// The states of subscriptions and instances are not shown here; counts are.
// Quantities are rendered from their decimal strings without ever becoming
// numbers — see data/format.ts.

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@miq/design-system/components/ui/card';
import { Badge } from '@miq/design-system/components/ui/badge';
import { Button } from '@miq/design-system/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@miq/design-system/components/ui/table';

import { BellRing } from 'lucide-react';
import { useCallback, useState } from 'react';

import type { AuthorizedFetch } from '../auth/api';
import { ScreenFailed, ScreenLoading } from '../components/ScreenState';
import { ShowFormerToggle } from '../components/ShowFormerToggle';
import { StatusCell } from '../components/StatusCell';
import { ABSENT, quantityText, usageHourLabel } from '../data/format';
import { when } from '../data/instants';
import {
  readAllCustomers,
  type AllCustomersScreen,
  type CustomerSummary,
} from '../data/acrossCustomers';
import { usePortalRead } from '../data/usePortalRead';
import { useDocumentTitle } from '../../lib/documentTitle';

const TITLE = 'All Customers';

export function AllCustomersPage({
  onOpenCustomer,
  onOpenAudit,
  onOpenAlerts,
}: {
  onOpenCustomer: (publicId: string) => void;
  onOpenAudit: () => void;
  onOpenAlerts: () => void;
}) {
  useDocumentTitle(TITLE);
  // Former customers are hidden by default and come back under the toggle.
  // Memoised per setting because `usePortalRead` holds the read in a
  // dependency array; flipping the toggle is exactly when it should re-run.
  const [showFormer, setShowFormer] = useState(false);
  const read = useCallback(
    (authorizedFetch: AuthorizedFetch) => readAllCustomers(authorizedFetch, showFormer),
    [showFormer],
  );
  const screen = usePortalRead(read);

  if (screen.status === 'loading') {
    return <ScreenLoading title={TITLE} />;
  }
  if (screen.status === 'failed') {
    // Includes the 404 this endpoint answers a caller who may not look. It is
    // a refusal, not a missing page, and it is shown in the service's own
    // words rather than translated into a guess about which of the two it was.
    return <ScreenFailed title={TITLE} message={screen.message} />;
  }
  return (
    <AllCustomers
      screen={screen.data}
      showFormer={showFormer}
      onShowFormer={setShowFormer}
      onOpenCustomer={onOpenCustomer}
      onOpenAudit={onOpenAudit}
      onOpenAlerts={onOpenAlerts}
    />
  );
}

function AllCustomers({
  screen,
  showFormer,
  onShowFormer,
  onOpenCustomer,
  onOpenAudit,
  onOpenAlerts,
}: {
  screen: AllCustomersScreen;
  showFormer: boolean;
  onShowFormer: (showing: boolean) => void;
  onOpenCustomer: (publicId: string) => void;
  onOpenAudit: () => void;
  onOpenAlerts: () => void;
}) {
  const { customers, open_alerts: alerts } = screen;
  // What the toggle governs: the rows the default hid, or — once shown — the
  // former rows on the screen, which are the ones with either mark set.
  const hiddenCount = showFormer
    ? customers.filter((c) => c.retired !== null || c.superseded_by !== null).length
    : screen.former_hidden;

  return (
    <div className="space-y-5 p-6">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          metricsIQ · Across every customer
        </div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">{TITLE}</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Every customer this service holds, as of {when(screen.as_of)}. Reads on
          this screen are logged —{' '}
          <Button
            variant="link"
            className="h-auto p-0 text-sm"
            onPress={onOpenAudit}
          >
            see the trail
          </Button>
          .
        </p>
      </div>

      {/* The alerts live on their own screen now. They were a card here,
          and the sweep raises one per instance per silent hour with nothing
          resolving them but the missing event arriving — so an unbounded
          list sat above a bounded one and pushed the customer list off the
          page within a day of the estate being registered. What stays is a
          line saying how many there are and where to read them. */}
      {alerts.length > 0 && (
        <Card className="border-warning/30 bg-warning-soft">
          <CardContent className="flex flex-wrap items-center gap-2 px-6 py-4 text-sm">
            <BellRing className="size-4 shrink-0 text-warning" />
            <span className="font-medium">
              {alerts.length === 1
                ? '1 open metering alert'
                : `${alerts.length} open metering alerts`}
            </span>
            <span className="text-muted-foreground">
              across the fleet — an instance that stopped reporting.
            </span>
            <Button
              variant="link"
              className="h-auto p-0"
              onPress={onOpenAlerts}
            >
              Open Metering Alerts
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">Customers</CardTitle>
            <CardDescription>
              {customers.length === 1 ? '1 customer' : `${customers.length} customers`}.
              Usage totals are every hour ever recorded, not this month's.
            </CardDescription>
          </div>
          {/* Former customers — retired or superseded — are kept, not deleted,
              and hidden by default: this list is who we serve. The toggle says
              how many it holds and is not drawn when there are none. */}
          <ShowFormerToggle
            showing={showFormer}
            hiddenCount={hiddenCount}
            onChange={onShowFormer}
            of="customers"
          />
        </CardHeader>
        <CardContent className="p-0">
          {customers.length === 0 ? (
            // A service holding no customers yet is a LOADED screen with
            // nothing in it, which is the state a fresh deployment is
            // genuinely in — not a failure and not a spinner.
            <p className="px-6 py-8 text-sm text-muted-foreground">
              No customers yet.
            </p>
          ) : (
            <CustomerTable
              customers={customers}
              onOpenCustomer={onOpenCustomer}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** The customer table: one row per customer, whatever its status. */
function CustomerTable({
  customers,
  onOpenCustomer,
}: {
  customers: CustomerSummary[];
  onOpenCustomer: (publicId: string) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Customer</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Sold through</TableHead>
            <TableHead className="text-right">Subscriptions</TableHead>
            <TableHead className="text-right">Instances</TableHead>
            <TableHead className="text-right">Usage total</TableHead>
            <TableHead>Latest usage hour</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customers.map((customer) => (
            <CustomerRow
              key={customer.public_id}
              customer={customer}
              onOpenCustomer={onOpenCustomer}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/**
 * Channels that can never bill, because no adapter is registered for them.
 *
 * Not a list of "internal things" — a list of channels `adapter_for()` raises
 * on. `internal` is a tenant that precedes its sale; `seeded` is the door a
 * deployment hands itself. Both are real rows doing real work, and neither is
 * revenue. A channel absent from here is one a submission could reach, so a
 * new channel reads as billable until somebody deliberately says otherwise —
 * which is the safe direction for a mistake on a screen about money.
 */
const NEVER_BILLS = new Set(['internal', 'seeded']);

/**
 * How a customer was sold to, said honestly when there is more than one answer.
 *
 * A customer may hold subscriptions on several channels, so this names every
 * one rather than picking a winner. The badge is muted only when EVERY channel
 * can never bill: a customer holding both a marketplace agreement and an
 * internal row is a paying customer, and dimming them because of the internal
 * half would be the same false reassurance in the other direction.
 */
function ChannelCell({ channels }: { channels: string[] }) {
  if (channels.length === 0) {
    // No subscriptions at all. Distinct from "sold through nothing" — see
    // the superseded rows, which is the usual way to arrive here.
    return <span className="text-muted-foreground">{ABSENT}</span>;
  }

  const everyChannelIsUnbillable = channels.every((c) => NEVER_BILLS.has(c));

  return (
    <div className="flex flex-wrap items-center gap-1">
      {channels.map((channel) => (
        <Badge
          key={channel}
          variant={everyChannelIsUnbillable ? 'secondary' : 'default'}
        >
          {channel}
        </Badge>
      ))}
      {everyChannelIsUnbillable && (
        <span className="text-xs text-muted-foreground">never bills</span>
      )}
    </div>
  );
}

function CustomerRow({
  customer,
  onOpenCustomer,
}: {
  customer: CustomerSummary;
  onOpenCustomer: (publicId: string) => void;
}) {
  return (
    <TableRow>
      <TableCell>
        <Button
          variant="link"
          className="h-auto justify-start p-0 font-medium"
          onPress={() => onOpenCustomer(customer.public_id)}
        >
          {customer.display_name}
        </Button>
        <div className="font-mono text-xs text-muted-foreground">
          {customer.public_id}
        </div>
      </TableCell>
      <TableCell>
        {/* Active, Retired <date> or Superseded → <successor>, from the two
            facts on the row. The successor opens in the same drill-in this
            row's name does. */}
        <StatusCell
          retired={customer.retired}
          supersededBy={customer.superseded_by}
          onOpenCustomer={onOpenCustomer}
        />
      </TableCell>
      <TableCell>
        <ChannelCell channels={customer.channels} />
      </TableCell>
      <TableCell className="text-right">{customer.subscription_count}</TableCell>
      <TableCell className="text-right">{customer.instance_count}</TableCell>
      <TableCell className="text-right font-medium">
        {/* Never a zero where there is nothing: a customer who has metered
            nothing and a customer whose metering totalled zero are different
            facts, and the endpoint sends null for the first. */}
        {customer.usage_total === null ? (
          <span className="text-muted-foreground">{ABSENT}</span>
        ) : (
          quantityText(customer.usage_total)
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {usageHourLabel(customer.latest_usage_hour)}
      </TableCell>
    </TableRow>
  );
}
